import { memo } from "react";
import type { NearbyState } from "../hooks/useGeolocation";
import { cn } from "../lib/classNames";

interface Props {
  state: NearbyState;
  /** 現在地取得を開始する。idle / error 時にタップで呼ばれる。 */
  onRequest: () => void;
  /** nearby mode を解除。active 時にタップで呼ばれる。 */
  onClear: () => void;
}

/**
 * 現在地ボタン (Floating Action Button)。
 *
 * 動作:
 *   off / error → タップで `onRequest` → loading → 取得成功で active
 *   loading     → 何もしない (再タップ防止)
 *   active      → タップで `onClear` (nearby mode 解除)
 *
 * 視覚:
 *   off     : 暗い丸 + 白 crosshair アイコン
 *   loading : 回転スピナー
 *   active  : sky カラーで強調 (＋現在地に寄せている状態を示す)
 *   error   : rose 系で警告表示 + tooltip でメッセージ
 */
export const LocateButton = memo(function LocateButton({
  state,
  onRequest,
  onClear,
}: Props) {
  const status = state.status;
  const busy = status === "loading";
  const active = status === "active";
  const errored = status === "error";
  // ブラウザレベルで権限拒否された場合、再リクエストしても即座に同じエラーが返り
  // 視覚的に flicker するだけの dead-end になる。この状態ではボタンを押せなくし、
  // 「ブラウザ設定から権限を戻してください」と案内する。
  const permanentlyDenied = status === "error" && state.code === "denied";

  const handleClick = () => {
    if (busy) return;
    if (permanentlyDenied) return;
    if (active) onClear();
    else onRequest();
  };

  const title =
    status === "active"
      ? "現在地モードを解除"
      : status === "loading"
        ? "現在地を取得中…"
        : permanentlyDenied
          ? "位置情報がブロックされています。ブラウザの設定から許可してください。"
          : status === "error"
            ? state.message
            : "現在地から周辺のラーメン屋を探す";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy || permanentlyDenied}
      aria-pressed={active}
      aria-label={title}
      title={title}
      className={cn(
        "pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border shadow-lg shadow-black/50 transition active:scale-95",
        active
          ? "border-sky-300/60 bg-sky-500/90 text-white"
          : errored
            ? "border-rose-400/50 bg-slate-900/95 text-rose-300"
            : "border-white/15 bg-slate-900/95 text-slate-100 hover:bg-slate-800/95",
        busy && "cursor-wait opacity-80",
        permanentlyDenied && "cursor-not-allowed opacity-70",
      )}
    >
      {busy ? (
        // スピナー (CSS animate-spin)
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          aria-hidden
          className="animate-spin"
        >
          <path d="M21 12a9 9 0 1 1-6.22-8.56" />
        </svg>
      ) : (
        // Crosshair + dot (Google Maps の「現在地」ボタンと同系アイコン)
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="8" />
          <line x1="12" y1="2" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22" y2="12" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
        </svg>
      )}
    </button>
  );
});
